import { memo } from "react";
import { Tooltip } from "antd";
import { PaneType } from "@unlikelyai-magic/shared/data-access/api";
import { cn } from "@unlikelyai-magic/utils";

export const PaneTypeColors: Record<PaneType, string> = {
  RULE: "bg-yellow-100",
  DEFINITION: "bg-green-100",
  GUIDANCE: "bg-violet-100",
  EXAMPLE: "bg-blue-100",
  CONTEXT: "bg-pink-100",
  NOT_APPLICABLE: "bg-gray-100 node-striped-background",
  PROHIBITION: "bg-gray-100 node-striped-background",
  COMPILED_RULE: "bg-gray-100 node-striped-background",
};

const PaneTypeToHighlightKeyMap: Record<PaneType, string> = {
  RULE: "REQUIREMENT",
  GUIDANCE: "INSTRUCTION",
  DEFINITION: "DEFINITION",
  EXAMPLE: "EXAMPLE",
  CONTEXT: "CONTEXT",
  NOT_APPLICABLE: "NOT_APPLICABLE",
  PROHIBITION: "PROHIBITION",
  COMPILED_RULE: "COMPILED_RULE",
};

/**
 * Evaluation example type for confidence calculation.
 * This is optional - consumers can provide this data if they have it.
 */
export interface EvaluationExample {
  predictionValue?: unknown;
  groundTruthValue?: unknown;
}

export type BlurbNodeProps = {
  id: string;
  data: {
    productTreeId: string;
    type: PaneType;
    paneId: string;
    blurbInformation: string;
    hasTree: boolean;
    applicability?: boolean;
    applicabilityExplanation?: string;
    /**
     * Optional evaluation examples for confidence calculation.
     * If not provided, the confidence indicator will not be shown.
     */
    evaluationExamples?: EvaluationExample[];
  };
  className?: string;
};

export const BlurbNode = memo(({ data }: BlurbNodeProps) => {
  // Use provided evaluation examples or empty array
  const allExamples = data.evaluationExamples ?? [];
  const evaluatedExamples = allExamples.filter(
    (example) => example.predictionValue != null
  );

  const correctFraction = [
    evaluatedExamples.filter(
      (example) => example.predictionValue === example.groundTruthValue
    ).length,
    evaluatedExamples.length,
  ];

  return (
    <div className="flex items-end gap-4 min-w-0 w-full">
      <div className="flex-1 min-w-0 overflow-hidden">
        <TypeLabel type={data.type} />
      </div>
      {correctFraction[1] > 0 &&
        correctFraction[0] / correctFraction[1] < 0.5 && (
          <Tooltip title="Low accuracy when evaluated against synthetic test data">
            <div
              className={
                "flex items-center justify-center flex-shrink-0 px-4 py-2 rounded-xl text-white bg-red-500 whitespace-nowrap text-sm"
              }
            >
              <span className="font-semibold">{"Low Confidence"}</span>
            </div>
          </Tooltip>
        )}
    </div>
  );
});

export const TypeLabel = ({ type }: { type: PaneType }) => {
  return (
    <div
      className={cn(
        "px-6 py-3 rounded capitalize text-center font-semibold text-3xl truncate max-w-full",
        PaneTypeColors[type]
      )}
    >
      {PaneTypeToHighlightKeyMap[type].replace("_", " ")}
    </div>
  );
};

export const ApplicabilityLabel = ({
  applicability,
}: {
  applicability: boolean;
}) => {
  return (
    <div
      className={cn(
        "px-2 py-1 w-fit rounded text-center",
        applicability ? "bg-green-100" : "bg-red-100"
      )}
    >
      <span className="font-semibold text-xs capitalize">
        {applicability ? "Applicable" : "Not Applicable"}
      </span>
    </div>
  );
};
